import COS, { ProgressInfo } from 'cos-js-sdk-v5';

import { CDN_PATH, COS_BUCKET, COS_REGION, DEFAULT_UUID, UPLOAD_PATH } from '@/const/cdn';
import { LIMIT_AUDIO, LIMIT_PIC, LIMIT_VIDEO, MAX_UPLOAD_SIZE, PICBOOK_IMAGE_SIZE } from '@/const/form';
import { ResourceTabType, UserResourceType } from '@/interface/library';
import { getUserId } from '@/login/login';
import { putCos } from '@/put-cos';
import { CgiErrorCode } from '@/services/cgi';
import { batchSetResource, setResource } from '@/services/story';
import { isPic } from '@/utils/getApiDomain';

import { ISpineResult } from './folder';
import { getUploadSpineObject } from './spine/utils/getUploadSpineObject';
import { ExistedFileInfo, HashFile, uploadFileMatch, uploadSpineMatch } from './uploadFileMatch';

const ignoreWebp = ['image/gif']; // gif不转webp

export const enum BatchUploadStatus {
  fail = 1,
  partFail = 2,
  success = 3
}

export const enum BatchUploadType {
  image = 1,
  audio = 2,
  video = 3,
  spine = 4,
}

export interface UploadOptions {
  storyId?: string, // 故事维度去重处理
  size?: number, // 上传大小限制
  type?: BatchUploadType, // 批量上传类型
}

export const getLimit = (type: UserResourceType, tabType?: ResourceTabType) => {
  // 绘本单独处理【目前仅 image size 有区别】
  if (tabType === ResourceTabType.TAB_TYPE_PIC) {
    switch (type) {
      case UserResourceType.RESOURCE_TYPE_AUDIO:
        return {
          fileType: LIMIT_AUDIO.type,
          size: LIMIT_AUDIO.size,
          uploadType: LIMIT_AUDIO.uploadType,
          type: BatchUploadType.audio,
        };
      case UserResourceType.RESOURCE_TYPE_VIDEO:
        return {
          fileType: LIMIT_VIDEO.type,
          size: LIMIT_VIDEO.size,
          uploadType: LIMIT_VIDEO.uploadType,
          type: BatchUploadType.video,
        };
      case UserResourceType.RESOURCE_TYPE_IMAGE:
      default:
        return {
          fileType: LIMIT_PIC.type,
          size: PICBOOK_IMAGE_SIZE,
          uploadType: LIMIT_PIC.uploadType,
          type: BatchUploadType.image,
        };
    }
  }

  // 正常情况
  switch (type) {
    case UserResourceType.RESOURCE_TYPE_AUDIO:
      return {
        fileType: LIMIT_AUDIO.type,
        size: LIMIT_AUDIO.size,
        uploadType: LIMIT_AUDIO.uploadType,
        type: BatchUploadType.audio,
      };
    case UserResourceType.RESOURCE_TYPE_VIDEO:
      return {
        fileType: LIMIT_VIDEO.type,
        size: LIMIT_VIDEO.size,
        uploadType: LIMIT_VIDEO.uploadType,
        type: BatchUploadType.video,
      };
    case UserResourceType.RESOURCE_TYPE_IMAGE:
    default:
      return {
        fileType: LIMIT_PIC.type,
        size: LIMIT_PIC.size,
        uploadType: LIMIT_PIC.uploadType,
        type: BatchUploadType.image,
      };
  }
};

// 获取用户ID的辅助函数，确保在调用时获取最新的cookie值
const getUuid = () => getUserId() || DEFAULT_UUID;

// 上传单个文件至cos
export const uploadCos = async (
  file: File,
  options?: UploadOptions,
): Promise<any> => {
  const {
    existedFiles = [],
    toUploadFiles = [],
  } = await uploadFileMatch([file], options?.storyId);

  // 如果文件已存在，则直接返回
  if (!toUploadFiles.length) {
    const { url, hash } = existedFiles[0];
    return {
      status: 'success',
      response: {
        url,
        localPath: hash,
      },
    };
  }

  // 上传新文件
  const uuid = getUuid();
  const key = `${UPLOAD_PATH}/${uuid}/`;
  const sizeLimit = options?.size || MAX_UPLOAD_SIZE;
  const localPath = toUploadFiles[0].hash;
  try {
    const res = await putCos().uploadFile({
      Bucket: COS_BUCKET, /* 填写自己的 bucket，必须字段 */
      Region: COS_REGION,    /* 存储桶所在地域，必须字段 */
      Key: key + localPath,      /* 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段 */
      Body: toUploadFiles[0].file,
      SliceSize: 1024 * 1024 * sizeLimit,
    });
    if (res?.statusCode === 200) {
      const url = `${CDN_PATH}/${key}${localPath}`;

      // 资源标记
      if (options?.storyId) {
        const { code, message } = await setResource({
          storyId: options?.storyId,
          hash: localPath,
          data: { cosUrl: url },
        });
        if (code !== CgiErrorCode.NO_ERROR) {
          console.error('资源标记失败:', message);
        }
      }

      return {
        status: 'success',
        response: {
          url,
          localPath,
        },
      };
    }
    return {
      status: 'fail',
      response: {},
    };
  } catch (error) {
    return {
      status: 'fail',
      response: {},
    };
  }
};

// 上传单个图片至cos并压缩
export const uploadCosImage = async (
  file: File,
  options?: UploadOptions,
): Promise<any> => {
  const {
    existedFiles = [],
    toUploadFiles = [],
  } = await uploadFileMatch([file], options?.storyId);

  // 如果图片已存在，则直接返回
  if (!toUploadFiles.length) {
    const { url, hash } = existedFiles[0];
    return {
      status: 'success',
      response: {
        url,
        localPath: hash,
      },
    };
  }

  // 上传新图片
  const uuid = getUuid();
  const key = `${UPLOAD_PATH}/${uuid}/`;
  const sizeLimit = options?.size || MAX_UPLOAD_SIZE;
  const localPath = toUploadFiles[0].hash;
  try {
    // 图片上传 cos 时转 webp（绘本除外）
    const header = !isPic && !ignoreWebp.includes(file.type) ? {
      'Pic-Operations': JSON.stringify({
        is_pic_info: 1, // 固定
        rules: [{
          fileid: localPath,
          rule: 'imageMogr2/format/webp',
        }],
      }),
    } : {};

    const res = await putCos().uploadFile({
      Bucket: COS_BUCKET,
      Region: COS_REGION,
      Key: key + localPath,
      Body: toUploadFiles[0].file,
      SliceSize: 1024 * 1024 * sizeLimit,
      Headers: header,
    });
    if (res?.statusCode === 200) {
      const url = `${CDN_PATH}/${key}${localPath}`;

      // 资源标记
      if (options?.storyId) {
        const { code, message } = await setResource({
          storyId: options?.storyId,
          hash: localPath,
          data: { cosUrl: url },
        });
        if (code !== CgiErrorCode.NO_ERROR) {
          console.error('资源标记失败:', message);
        }
      }

      return {
        status: 'success',
        response: {
          url,
          localPath,
        },
      };
    }
    return {
      status: 'fail',
      response: {},
    };
  } catch (error) {
    return {
      status: 'fail',
      response: {},
    };
  }
};

// 批量上传
export const batchUploadCos = async (
  files: any[],
  options: UploadOptions,
  onProgress: Function,
  callback: Function,
): Promise<any> => {
  try {
    const {
      hashFiles = [],
      existedFiles = [],
      toUploadFiles = [],
    } = await uploadFileMatch(files.map(e => e?.raw), options.storyId);

    // 如果文件均已存在，则直接返回
    if (!toUploadFiles.length) {
      const successFiles = getSuccessFiles(hashFiles, existedFiles);
      callback({
        code: BatchUploadStatus.success,
        msg: '上传成功',
        urls: successFiles,
      });
      return;
    }

    // 上传剩余文件
    const uuid = getUuid();
    const key = `${UPLOAD_PATH}/${uuid}/`;
    const sizeLimit = options.size || MAX_UPLOAD_SIZE;
    const uploadFiles = toUploadFiles.map((e) => {
      const { file } = e;
      const localPath = e.hash;
      const common = {
        Bucket: COS_BUCKET,
        Region: COS_REGION,
        Key: key + localPath,
        Body: file,
        name: file.name,
        hash: localPath,
        SliceSize: 1024 * 1024 * sizeLimit,
      };

      if (options.type === BatchUploadType.image && !isPic && !ignoreWebp.includes(file?.type)) {
        return {
          ...common,
          Headers: {
            'Pic-Operations': JSON.stringify({
              is_pic_info: 1, // 固定
              rules: [{
                fileid: localPath,
                rule: 'imageMogr2/format/webp',
              }],
            }),
          },
        };
      }

      return common;
    });

    putCos().uploadFiles(
      {
        files: uploadFiles,
        SliceSize: 1024 * 1024 * sizeLimit,
        onProgress: (info: ProgressInfo) => handleProgress(info, onProgress),
        onFileFinish(err, _data, options) {
          console.log(`${options.Key}: 上传${err ? '失败' : '完成'}`);
        },
      },
      async (err, data) => {
        if (err) console.error('upload error:', err);
        // 处理上传结果
        const { successList, failNames } = processUploadResults(data);
        const newUploads = successList.map(e => ({
          url: `${CDN_PATH}/${e.options.Key}`,
          hash: (e.options as unknown as { hash: string })?.hash,
        }));

        // 资源标记
        if (options.storyId) {
          await batchSetResource(newUploads, options.storyId);
        }

        const successFiles = getSuccessFiles(
          hashFiles,
          [...existedFiles, ...newUploads],
        );

        if (successFiles.length === 0) {
          callback({
            code: BatchUploadStatus.fail,
            msg: `上传失败【${failNames}】`,
            urls: [],
          });
        } else if (successFiles.length !== files.length) {
          callback({
            code: BatchUploadStatus.partFail,
            msg: `部分失败【${failNames}】`,
            urls: successFiles,
          });
        } else {
          callback({
            code: BatchUploadStatus.success,
            msg: '上传成功',
            urls: successFiles,
          });
        }
      },
    );
  } catch (error) {
    return {
      status: 'fail',
      response: {},
    };
  }
};

// spine上传 (文件夹整体上传)
export const uploadCosSpine = async (
  files: any[],
  options: UploadOptions,
  onProgress: Function,
  callback: Function,
): Promise<any> => {
  try {
    const {
      existedFiles = [],
      toUploadFiles = [],
      folderName = '',
      folderHash = '',
    } = await uploadSpineMatch(files, options.storyId);
    const uuid = getUuid();
    const directoryPath = `${CDN_PATH}/${UPLOAD_PATH}/${uuid}/spine/${folderHash}`;

    // 如果存在spine数据，则直接返回
    if (existedFiles[0].spine && !toUploadFiles.length) {
      const spine: ISpineResult = {
        ...existedFiles[0].spine,
        localPath: folderHash,
        directoryName: folderName,
        directoryPath,
      };

      callback({
        code: BatchUploadStatus.success,
        msg: '上传成功',
        spine,
      });
      return;
    }

    const key = `${UPLOAD_PATH}/${uuid}/spine/${folderHash}/`;
    const sizeLimit = options.size || MAX_UPLOAD_SIZE;
    const uploadFiles = toUploadFiles.map((e) => {
      const { file } = e;
      const localPath = e.hash;
      const common = {
        Bucket: COS_BUCKET,
        Region: COS_REGION,
        Key: key + file?.name,
        Body: file,
        name: file?.name,
        hash: localPath,
        SliceSize: 1024 * 1024 * sizeLimit,
      };

      return common;
    });

    putCos().uploadFiles(
      {
        files: uploadFiles,
        SliceSize: 1024 * 1024 * sizeLimit,
        onProgress: (info: ProgressInfo) => handleProgress(info, onProgress),
        onFileFinish(err, _data, options) {
          console.log(`${options.Key}: 上传${err ? '失败' : '完成'}`);
        },
      },
      async (err, data) => {
        if (err) console.error('upload spine error:', err);
        // 处理上传结果
        const { successList, failNames } = processUploadResults(data);
        const successUrls = successList.map(e => ({
          url: `${CDN_PATH}/${e.options.Key}`,
          resourceName: (e?.options as unknown as { name: string; })?.name,
        }));

        // 获取spine数据
        const obj = getUploadSpineObject(successUrls);
        const spineData: ISpineResult = {
          ...obj,
          localPath: folderHash,
          directoryName: folderName,
          directoryPath,
        };

        // 资源标记
        if (options.storyId) {
          const { code, message } = await setResource({
            storyId: options.storyId,
            hash: folderHash,
            data: { spineData },
          });
          if (code !== CgiErrorCode.NO_ERROR) {
            console.error('资源标记失败:', message);
          }
        }

        if (successUrls.length === 0) {
          callback({
            code: BatchUploadStatus.fail,
            msg: `上传失败【${failNames}】`,
            urls: [],
          });
        } else if (successUrls.length !== files.length) {
          callback({
            code: BatchUploadStatus.partFail,
            msg: `部分失败【${failNames}】`,
            urls: successUrls,
          });
        } else {
          callback({
            code: BatchUploadStatus.success,
            msg: '上传成功',
            urls: successUrls,
            spine: spineData,
          });
        }
      },
    );
  } catch (error) {
    return {
      status: 'fail',
      response: {},
    };
  }
};

// 处理上传结果
const processUploadResults = (data: COS.UploadFilesResult) => {
  const successList = data.files.filter(e => !e.error);
  const failList = data.files.filter(e => e.error);
  const failNames = failList?.map(e => (e?.options as unknown as { name: string })?.name).join(', ');
  return { successList, failList, failNames };
};

// 获取成功上传的文件列表
const getSuccessFiles = (hashFiles: HashFile[], existedFiles: ExistedFileInfo[]) => {
  const existedMap = new Map(existedFiles.map(e => [e.hash, e]));
  return hashFiles
    .map(({ hash, file }) => {
      const existed = existedMap.get(hash);
      return existed?.url
        ? { url: existed.url, resourceName: file.name }
        : undefined;
    })
    .filter(e => !!e);
};

// 批量上传处理进度
const handleProgress = (info: ProgressInfo, callback: Function) => {
  const percent = parseInt(`${info.percent * 10000}`, 10) / 100;
  const speed = parseInt(`${info.speed / 1024 / 1024 * 100}`, 10) / 100;
  callback(`${percent > 99 ? 99 : percent}%`);
  console.log(`文件上传进度：${percent}%; 速度：${speed}Mb/s;`);
};
